import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Layout from './Layout';
import CustomerModal from './CustomerModal';
import PageHeader from './PageHeader';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
  Pagination
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { fetchSales, createSale, searchCustomers, searchProductVariants } from '../api';

const SalesPage = () => {
  const [totalPayableAmount, setTotalPayableAmount] = useState(0);
  const [sales, setSales] = useState([]);
  const [nextPage, setNextPage] = useState(null);
  const [previousPage, setPreviousPage] = useState(null);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [saleItems, setSaleItems] = useState([]);
  const [activateAddItemButton, setActivateAddItemButton] = useState(true);
  const [productVariantSearchTerm, setproductVariantSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [overallDiscount, setOverallDiscount] = useState(0);
  const [paidAmount, setPaidAmount] = useState(0);
  const [paymentReference, setPaymentReference] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [customerModalVisibility, setCustomerModalVisibility] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [salesPage, setSalesPage] = useState(1);
  const SALES_PER_PAGE = 10;

  const toggleCustomerModalVisibility = () => {
    setCustomerModalVisibility(!customerModalVisibility);
  };

  useEffect(() => {
    fetchSalesPage(salesPage);
  }, [salesPage]);

  const fetchSalesPage = (page) => {
    setLoading(true);
    fetchSales(page)
      .then((response) => {
        setSales(response.data.results);
        setNextPage(response.data.next);
        setPreviousPage(response.data.previous);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching sales:', error);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (customerSearchTerm) {
      searchCustomers(customerSearchTerm)
        .then((response) => {
          setFilteredCustomers(response.data.results || response.data);
        })
        .catch((error) => {
          console.error('Error fetching customers:', error);
        });
    } else {
      setFilteredCustomers([]);
    }
  }, [customerSearchTerm]);

  useEffect(() => {
    if (productVariantSearchTerm) {
      searchProductVariants(productVariantSearchTerm)
        .then((response) => {
          setFilteredProducts(response.data.results || response.data);
        })
        .catch((error) => {
          console.error('Error fetching products:', error);
        });
    } else {
      setFilteredProducts([]);
    }
  }, [productVariantSearchTerm]);

  const handleCreateSale = (e) => {
    e.preventDefault();
    saleItems.forEach(function (saleItem) {
      delete saleItem.is_added;
      delete saleItem.product_details;
    });

    const saleData = {
      customer: selectedCustomer?.id,
      saleitems: saleItems,
      overall_discount: overallDiscount,
      paid_amount: paidAmount,
      payment_reference: paymentReference
    };

    createSale(saleData)
      .then(() => {
        setMessage('Sale created successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setSelectedCustomer(null);
        setSaleItems([]);
        setOverallDiscount(0);
        setPaidAmount(0);
        setPaymentReference('');
        fetchSalesPage(salesPage);
      })
      .catch((error) => {
        setMessage('Failed to create sale.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        console.error('Error creating sale:', error);
      });
  };

  const addSaleItem = () => {
    setSaleItems([
      ...saleItems,
      {
        product_variant: '',
        quantity: 1,
        discount: 0,
        is_added: false,
        product_details: { name: '' }
      }
    ]);
  };

  const handleSaleItemChange = (index, field, value) => {
    const updatedItems = [...saleItems];
    updatedItems[index][field] = value;
    setSaleItems(updatedItems);
  };

  const handleRemoveSaleItem = (index) => {
    const updatedItems = saleItems.filter((_, i) => i !== index);
    setSaleItems(updatedItems);
  };

  const calculateTotalPayableAmount = () => {
    let total = saleItems.reduce((acc, item) => {
      if (item.product_details && item.quantity > 0) {
        let priceAfterDiscount =
          (item.product_details.selling_price - item.discount) * item.quantity;
        return acc + priceAfterDiscount;
      }
      return acc;
    }, 0);
    total = total - overallDiscount;
    setTotalPayableAmount(total > 0 ? total : 0);
  };

  useEffect(() => {
    calculateTotalPayableAmount();
  }, [saleItems, overallDiscount]);

  if (loading) {
    return <Box p={4}><Typography>Loading...</Typography></Box>;
  }

  return (
    <Layout>
      <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 'lg', mx: 'auto' }}>
        <PageHeader title="Sales" subtitle="Create and manage sales transactions." />

        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Create a New Sale</Typography>
            {message && <Alert severity={snackbarSeverity} sx={{ mb: 2 }}>{message}</Alert>}
            <Box component="form" onSubmit={handleCreateSale}>
              <Stack spacing={2}>
                <Box display="flex" alignItems="center" gap={2}>
                  <TextField
                    label="Search customer by name, address, or mobile..."
                    value={customerSearchTerm}
                    onChange={(e) => setCustomerSearchTerm(e.target.value)}
                    disabled={!!selectedCustomer}
                    fullWidth
                  />
                  <Button variant="outlined" onClick={toggleCustomerModalVisibility} startIcon={<AddIcon />}>Add new customer</Button>
                </Box>
                {!selectedCustomer && filteredCustomers.length > 0 && (
                  <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', p: 1 }}>
                    {filteredCustomers.map((customer) => (
                      <Box
                        key={customer.id}
                        sx={{ cursor: 'pointer', p: 1, borderBottom: '1px solid #eee', mb: 1, borderRadius: 1, '&:hover': { background: '#f5f5f5' } }}
                        onClick={() => {
                          setSelectedCustomer(customer);
                          setCustomerSearchTerm('');
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box flex={1}>
                            <Typography fontWeight="bold" variant="subtitle1">{customer.name}</Typography>
                            <Typography variant="body2" color="text.secondary">📍 {customer.address}</Typography>
                            <Typography variant="body2" color="text.secondary">📞 {customer.phone}</Typography>
                            <Typography variant="body2" color="text.secondary">📧 {customer.email}</Typography>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Paper>
                )}
                {selectedCustomer && (
                  <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8f9fa', borderLeft: '4px solid #1976d2' }}>
                    <Box>
                      <Typography fontWeight="bold" variant="subtitle1">{selectedCustomer.name}</Typography>
                      <Typography variant="body2" color="text.secondary">📍 {selectedCustomer.address}</Typography>
                      <Typography variant="body2" color="text.secondary">📞 {selectedCustomer.phone}</Typography>
                      <Typography variant="body2" color="text.secondary">📧 {selectedCustomer.email}</Typography>
                    </Box>
                    <IconButton onClick={() => setSelectedCustomer(null)}><CloseIcon /></IconButton>
                  </Paper>
                )}
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="h6">Total Payable Amount: <span>{totalPayableAmount.toFixed(2)}</span></Typography>
                  <Button variant="contained" onClick={() => { addSaleItem(); setActivateAddItemButton(false); }} disabled={!activateAddItemButton} startIcon={<AddIcon />}>Add Item</Button>
                </Box>
                <Stack spacing={2}>
                  {saleItems.map((item, index) => (
                    <Paper key={index} variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                      {!saleItems[index].is_added && (
                        <Box flex={2}>
                          <TextField
                            label="Search product by name or description"
                            value={productVariantSearchTerm}
                            onChange={(e) => setproductVariantSearchTerm(e.target.value)}
                            disabled={saleItems[index].is_added}
                            required
                            fullWidth
                          />
                          {filteredProducts.length > 0 && (
                            <Paper variant="outlined" sx={{ maxHeight: 120, overflow: 'auto', mt: 1 }}>
                              {filteredProducts.map((product_variant) => (
                                <Box
                                  key={product_variant.id}
                                  sx={{ cursor: 'pointer', p: 1, borderBottom: '1px solid #eee' }}
                                  onClick={() => {
                                    handleSaleItemChange(index, 'product_variant', product_variant.id);
                                    handleSaleItemChange(index, 'product_details', product_variant);
                                    handleSaleItemChange(index, 'is_added', true);
                                    setActivateAddItemButton(true);
                                    setproductVariantSearchTerm('');
                                  }}
                                >
                                  <Typography fontWeight="bold">{product_variant.product_name}</Typography>
                                  <Typography variant="body2">Size: {product_variant.size} | Color: {product_variant.color} | Stock: {product_variant.stock} | Selling at: {product_variant.selling_price}</Typography>
                                </Box>
                              ))}
                            </Paper>
                          )}
                        </Box>
                      )}
                      {saleItems[index].is_added && (
                        <Box flex={2}>
                          <Typography>🛒: <b>{saleItems[index].product_details.product_name}</b> | 📏 : {saleItems[index].product_details.size} | 🔴🟢🔵 : {saleItems[index].product_details.color} | 💵 : {saleItems[index].product_details.selling_price}</Typography>
                        </Box>
                      )}
                      <TextField
                        type="number"
                        label="Quantity"
                        value={item.quantity}
                        onChange={(e) => handleSaleItemChange(index, 'quantity', e.target.value)}
                        inputProps={{ min: 1 }}
                        required
                        sx={{ width: 120 }}
                      />
                      <TextField
                        type="number"
                        label="Discount per item"
                        value={item.discount}
                        onChange={(e) => handleSaleItemChange(index, 'discount', e.target.value)}
                        inputProps={{ min: 0, step: 1 }}
                        sx={{ width: 150 }}
                      />
                      <IconButton color="error" onClick={() => { handleRemoveSaleItem(index); setActivateAddItemButton(true); }}><CloseIcon /></IconButton>
                    </Paper>
                  ))}
                </Stack>
                {/* Grouped payment fields */}
                <Paper variant="outlined" sx={{ p: 2, mt: 2, mb: 2, background: '#f5f7fa' }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                    Payment & Discount
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      type="number"
                      label="Overall Discount"
                      value={overallDiscount}
                      onChange={(e) => setOverallDiscount(e.target.value)}
                      inputProps={{ min: 0, step: 1 }}
                      sx={{ width: { xs: '100%', sm: 200 } }}
                    />
                    <TextField
                      type="number"
                      label="Paid Amount"
                      value={paidAmount}
                      onChange={(e) => setPaidAmount(e.target.value)}
                      inputProps={{ min: 0, step: 1 }}
                      sx={{ width: { xs: '100%', sm: 200 } }}
                    />
                    <TextField
                      label="Payment Reference"
                      value={paymentReference}
                      onChange={(e) => setPaymentReference(e.target.value)}
                      sx={{ width: { xs: '100%', sm: 300 } }}
                    />
                  </Stack>
                </Paper>
                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={saleItems.length === 0}
                    sx={{ minWidth: 180, maxWidth: 220 }}
                  >
                    Create Sale
                  </Button>
                </Box>
              </Stack>
            </Box>
            <CustomerModal
              isOpen={customerModalVisibility}
              onClose={toggleCustomerModalVisibility}
            />
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>Sale List</Typography>
            <TableContainer component={Paper}>
              <Table size="small" sx={{ tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 80, whiteSpace: 'nowrap' }}>ID</TableCell>
                    <TableCell sx={{ width: 120, whiteSpace: 'nowrap' }}>Date</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Total Amount</TableCell>
                    <TableCell>Due Amount</TableCell>
                    <TableCell>Paid Amount</TableCell>
                    <TableCell>Overall Discount</TableCell>
                    <TableCell sx={{ minWidth: 200, maxWidth: 340, whiteSpace: 'normal', wordBreak: 'break-word' }}>Items</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sales.length > 0 ? (
                    sales.map((sale) => (
                      <TableRow key={sale.id}>
                        <TableCell>
                          <Button component={RouterLink} to={`/sales/${sale.id}`} size="small" sx={{ textTransform: 'none', p: 0, minWidth: 0 }}>
                            {sale.id}
                          </Button>
                        </TableCell>
                        <TableCell>
                          {formatSaleDateTime(sale.date)}
                        </TableCell>
                        <TableCell>{sale.customer_name}</TableCell>
                        <TableCell>{sale.total_amount}</TableCell>
                        <TableCell sx={{ color: sale.due_amount > 0 ? 'red' : 'green' }}>{sale.due_amount}</TableCell>
                        <TableCell>{sale.paid_amount}</TableCell>
                        <TableCell>{sale.overall_discount}</TableCell>
                        <TableCell>
                          {sale.saleitems.map((item, idx) => (
                            <div key={idx}>
                              {item.quantity}x {item.product_name} @ {item.price_per_unit - item.discount}
                              {item.discount > 0 ? ` ( - ${item.discount} )` : ''}
                              <br />
                            </div>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8}>No sales data available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={nextPage || sales.length === 0 ? salesPage + 1 : salesPage}
                page={salesPage}
                onChange={(e, value) => setSalesPage(value)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          </CardContent>
        </Card>

        <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)}>
          <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

// Helper to format ISO date string as 'YYYY-MM-DD\nT\nHH:MM:SSZ'
function formatSaleDateTime(isoString) {
  if (!isoString) return '';
  const [date, timeZ] = isoString.split('T');
  if (!timeZ) return isoString;
  const [time] = timeZ.split('.');
  const timePart = time ? time : '';
  return (
    <>
      {date}
      <br />
      T{timePart}Z
    </>
  );
}

export default SalesPage;
